import React from "react";

// Mui
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";

// Mui Styles
import {
  setDistributionData,
  setDistributionDataLoading,
  setDistributionDataOpen,
  setDistributionDataTableTitle,
  setLineChartData,
  setLineChartOpen,
  setSelectedLocation,
} from "@/features/productSalesSlice";
import { StyledPaper } from "@/styles/mui/globalStyles";
import { ButtonSendTheme, TableListTheme } from "@/styles/mui/tableListTheme";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  LineChart,
  lineElementClasses,
  markElementClasses,
} from "@mui/x-charts/LineChart";
import {
  GridToolbar,
  GridToolbarContainer,
  useGridApiContext,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";

// Components
import ProductAutoComplete from "@/components/Filter/productAutocomplete";
import DataTable from "@/components/dataTable";
import Layout from "@/components/layout";
import Title from "@/components/title";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import IconButton from "@mui/material/IconButton";
// Fetch
import {
  getAreaAndDistrict,
  getProducts,
  getProductSales,
  getProductTrend,
  getShopSales,
} from "@/fetch/fetchStatistics";
import { getRegionOptions } from "@/fetch/fetchWork";
import { cheerfulFiestaPalette } from "@mui/x-charts/colorPalettes";

import getDateRange from "@/lib/getDateRange";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import _ from "lodash";
// Moment
import moment from "moment";
import "moment/locale/zh-hk";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const CustomTabPanel = React.memo((props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
});
function a11yProps(index) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

const ProductSalesChartPage = (props) => {
  const { open, setOpen } = props;

  // Theme
  const theme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
    },
  });
  const distributionTableRef = React.useRef(null);
  const [slideTranOn, setSlideTranOn] = React.useState(false);
  const [dataTableLoading, setDataTableLoading] = React.useState(false);
  const salesData = React.useRef([]);
  const [amOptions, setAmOptions] = React.useState([]);
  const [allOptions, setAllOptions] = React.useState([]);
  const [districtOptions, setDistrictOptions] = React.useState([]);
  const [areaOptions, setAreaOptions] = React.useState([]);
  const [productOptions, setProductOptions] = React.useState([]);
  const [shopOptions, setShopOptions] = React.useState([]);
  const [displayMode, setDisplayMode] = React.useState(0);
  const [filterShopOptions, setFilterShopOptions] = React.useState([]);
  const [filterAreaOptions, setFilterAreaOptions] = React.useState([]);
  const [limit, setLimit] = React.useState(0);
  const [orderBy, setOrderBy] = React.useState("qty");
  const [ordering, setOrdering] = React.useState("desc");
  const [isLoading, setLoading] = React.useState(false);
  const [rowData, setRowData] = React.useState([]);

  const [sources, setSources] = React.useState("promoter");
  const [expanded, setExpanded] = React.useState(true);
  const [rawSalesData, setRawSalesData] = React.useState([]);
  const dispatch = useDispatch();

  const dataPickerEndRef = React.useRef(null);
  const dataPickerStartRef = React.useRef(null);

  const [dateRange, setDateRange] = React.useState("7 days");
  const [showRange, setShowRange] = React.useState(false);
  const [filter, setFilter] = React.useState({
    dateRange: "7 days",
    groupBy: "work_district",
    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    district: [],
    area: [],
    shop: [],
    product: [],
    groupByType: "product",
    am: [],
  });

  const handleData = (data) => {
    setLoading(false);
    const orderedData = _.orderBy(data, [orderBy], [ordering]);
    setRowData(orderedData);
  };
  const fetchProductSales = async (selectedFilter = filter) => {
    try {
      setDataTableLoading(true);
      const isManningData = sources === "mannings";
      const { data: sales } = await getProductSales(
        selectedFilter,
        isManningData
      );

      const { data: shopSales } = await getShopSales(
        selectedFilter,
        isManningData
      );
      salesData.current = sales;
      const groupByWorkLabels = _.groupBy(sales, "work_label");

      const sumByWorkLabel = _.map(shopSales, (shopSale) => {
        const groupByWorkLabel = groupByWorkLabels[shopSale.work_label];
        return {
          label: shopSale.work_label,
          amount: _.sumBy(groupByWorkLabel, (g) =>
            g.tx_date ? parseFloat(g.total) : 0
          ),
          qty: _.sumBy(groupByWorkLabel, (g) =>
            g.tx_date ? parseInt(g.qty) : 0
          ),
          total_amount: shopSale.amount,
          total_qty: shopSale.qty,
          region_id: shopSale.region_id,
          area_id: shopSale.area_id,
          work_label_id: shopSale.work_label_id,
          district_id: shopSale.district_id,
          work_location_id: shopSale.work_location_id,
        };
      });
      handleData(sumByWorkLabel, selectedFilter);
    } catch (error) {
    } finally {
      setLoading(false);
      setExpanded(false);
      setDataTableLoading(false);
    }
  };

  const handleGroupingChange = (e) => {
    setFilter((prev) => {
      return {
        ...prev,
        groupBy: e.target.value,
      };
    });
  };
  const handleGroupTypeChange = (e) => {
    setFilter((prev) => {
      return {
        ...prev,
        groupByType: e.target.value,
      };
    });
  };
  const handDateRangeChange = (e) => {
    switch (e.target.value) {
      case "custom":
        setShowRange(true);
        setFilter((prev) => {
          return {
            ...prev,
            dateRange: e.target.value,
          };
        });
        setTimeout(() => {
          dataPickerStartRef.current.parentElement.click();
        }, 100);

        break;
      case "7 days":
        setFilter((prev) => {
          return {
            ...prev,
            startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
            dateRange: "custom",
          };
        });
        break;
      case "30 days":
        setFilter((prev) => {
          return {
            ...prev,
            startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
            dateRange: "custom",
          };
        });
        break;
      case "6 months":
        setFilter((prev) => {
          return {
            ...prev,
            startDate: moment().subtract(6, "months").format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
            dateRange: "custom",
          };
        });
        break;
      case "1 years":
        setFilter((prev) => {
          return {
            ...prev,
            startDate: moment().subtract(1, "years").format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
            dateRange: "custom",
          };
        });
        break;
      default:
        break;
    }
    setDateRange(e.target.value);
  };
  const handleAMChange = (e) => {
    const filterOptions = _.filter(allOptions, (allOption) =>
      e.target.value.includes(allOption.user_id)
    );

    const filterDistrict = _.uniqBy(
      _.map(
        filterOptions.filter((option) =>
          e.target.value.includes(option.user_id)
        ),
        "district_id"
      )
    );
    const filterArea = areaOptions.filter((areaOption) =>
      filterDistrict.includes(areaOption.district_id)
    );
    const unDistrict = _.uniqBy(_.map(filterArea, "district_id"));
    const filterShop = shopOptions.filter((shopOption) =>
      unDistrict.includes(shopOption.work_district_id)
    );

    setFilter((prev) => {
      return {
        ...prev,
        am: e.target.value,
        district: filterDistrict,
        area: _.map(filterArea, "area_id"),
      };
    });
    setFilterAreaOptions(filterArea);
    setFilterShopOptions(filterShop);
  };
  const groupId =
    filter.groupByType === "product" ? "product_master_id" : "brand_id";
  const groupName =
    filter.groupByType === "product" ? "product_name" : "brand_name";
  const onRowClick = async (selectedFilter, label) => {
    try {
      dispatch(setDistributionDataOpen({ value: true }));
      dispatch(setDistributionDataLoading({ value: true }));
      const isManningData = sources === "mannings";
      dispatch(
        setSelectedLocation({
          district: selectedFilter.district,
          area: selectedFilter.area,
          shop: selectedFilter.shop,
        })
      );

      const { data: sales } = await getProductSales(
        selectedFilter,
        isManningData
      );
      const { data: shopSales } = await getShopSales(
        selectedFilter,
        isManningData
      );

      const groupByProducts = _.groupBy(sales, groupId);
      let title = ``;
      const sumByProducts = _.orderBy(
        _.map(groupByProducts, (groupByProduct) => {
          const {
            m18_product_code,
            brand_name,
            product_name,
            product_master_id,
            region_id,
            district_id,
            area_id,
            brand_id,
            work_location_id,
          } = groupByProduct[0];
          const totalData = _.find(
            shopSales,
            (shopSale) => shopSale.work_label === label
          );
          title = label;
          return {
            region_id,
            district_id,
            area_id,
            work_location_id,
            m18_product_code: m18_product_code,
            product_master_id: product_master_id,
            product_name: product_name,
            brand_name: brand_name,
            brand_id: brand_id,
            amount: _.sumBy(groupByProduct, (g) =>
              g.tx_date && g.total ? parseInt(g.total) : 0
            ),
            qty: _.sumBy(groupByProduct, (g) =>
              g.tx_date && g.qty ? parseInt(g.qty) : 0
            ),
            total_amount: totalData.amount,
            total_qty: totalData.qty,
          };
        }),
        [orderBy],
        [ordering]
      );

      dispatch(setDistributionDataTableTitle({ value: title }));

      dispatch(setDistributionData({ value: sumByProducts }));
      dispatch(setDistributionDataLoading({ value: false }));

      setExpanded(false);
    } catch (error) {}
  };
  const DistributionTable = React.memo(({ onClose }) => {
    const distributionDataOpen = useSelector(
      (state) => state.productSales.distributionDataOpen
    );
    const distributionDataLoading = useSelector(
      (state) => state.productSales.distributionDataLoading
    );
    const selectedLocation = useSelector(
      (state) => state.productSales.selectedLocation
    );
    const distributionDataTableTitle = useSelector(
      (state) => state.productSales.distributionDataTableTitle
    );
    const distributionData = useSelector(
      (state) => state.productSales.distributionData
    );

    const handleOnClose = () => {
      dispatch(setDistributionDataOpen({ value: false }));
      dispatch(setDistributionDataTableTitle({ value: "" }));

      dispatch(setDistributionData({ value: [] }));
    };
    const handleTrendData = async (sales, groupBy = "day") => {
      let format = "";

      switch (groupBy) {
        case "day":
          format = `YYYY-MM-DD`;
          break;
        case "week":
          format = `YY年第w[週]`;
          break;
        case "month":
          format = `YY年M月`;
          break;
        case "quarter":
          format = `YY年[Q]Q`;
          break;
        default:
          format = `YYYY-MM-DD`;
          break;
      }
      let xAxis = [
        {
          data: [],
          scaleType: "point",
          id: "axis1",
          valueFormatter: (code, context) => {
            const sale = sales.find((s) => s.tx_date === code);
            if (sale?.product_discount_price) {
              if (sale.discount_name) {
                return `${sale.tx_date}(${sale.discount_name})`;
              } else {
                return `${sale.tx_date}`;
              }
            }
            return code;
          },
        },
      ];
      const series = [];
      let labels = [];
      setLoading(false);
      switch (dateRange) {
        case "7 days":
        case "30 days":
        case "6 months":
        case "1 years":
          const splitDateRange = dateRange.split(" ");
          labels = getDateRange(
            moment().subtract(splitDateRange[0], splitDateRange[1]),
            moment(),
            groupBy
          );
          break;
        case "custom":
          labels = getDateRange(filter.startDate, filter.endDate, groupBy);
          break;
        default:
          break;
      }
      xAxis[0].data = labels;
      const groupByProduct = _.groupBy(sales, groupId);

      _.forEach(groupByProduct, (product, key) => {
        const label = product[0][groupName];

        const groupByLabel = _.groupBy(product, (p) =>
          moment(p.tx_date).format(format)
        );
        const seriesData = {
          id: key,
          data: [],
          label: label,
          baseline: "min",
          showMark: false,
          valueFormatter: (value) =>
            `$${new Intl.NumberFormat().format(value)}`,
        };
        _.forEach(labels, (label) => {
          if (groupByLabel[label] === undefined) {
            seriesData.data.push(0);
          } else {
            seriesData.data.push(
              _.sumBy(groupByLabel[label], (label) => parseInt(label.total))
            );
          }
          // series.push({});
        });
        series.push(seriesData);
      });
      let totalData = _.fill(Array(series[0].data.length), 0);
      _.forEach(series, (value, index) => {
        _.forEach(value.data, (v, k) => {
          totalData[k] += v;
        });
      });
      series.unshift({
        id: "total",
        data: totalData,
        label: "Total",
        baseline: "min",
        showMark: false,
        valueFormatter: (value) => `$${new Intl.NumberFormat().format(value)}`,
      });
      console.log("series", series);
      dispatch(
        setLineChartData({
          series,
          xAxis,
          height: 500,
          colors: cheerfulFiestaPalette,
        })
      );
      setExpanded(false);
      dispatch(setLineChartOpen({ value: true }));
    };

    const fetchProductTrend = async (filter) => {
      const isManningData = sources === "mannings";
      const { data: sales } = await getProductTrend(filter, isManningData);
      setRawSalesData(sales);
      handleTrendData(sales);
    };
    const CompareButton = () => {
      const apiRef = useGridApiContext();
      return (
        <GridToolbarContainer>
          <Button
            disabled={apiRef.current.getSelectedRows().length <= 1}
            onClick={async () => {
              const selectedRows = apiRef.current.getSelectedRows();
              const product_ids = [];
              selectedRows.forEach((selectedRow) => {
                product_ids.push(selectedRow[groupId]);
              });

              if (product_ids.length >= 2) {
                const selectedFilter = {
                  ...filter,
                  product: product_ids,
                  dateGrouping: "day",
                  district: selectedLocation.district,
                  area: selectedLocation.area,
                  shop: selectedLocation.shop,
                };
                await fetchProductTrend(selectedFilter);
              } else {
                window.alert("請選擇2個以上比較");
              }
            }}
            variant="contained"
          >
            比較
          </Button>
        </GridToolbarContainer>
      );
    };
    const distributionColumns = [
      {
        field: groupId,
        headerName: groupId,
        hide: true,
      },
      {
        field: "region_id",
        headerName: "region_id",
        hide: true,
      },
      {
        field: "district_id",
        headerName: "district_id",
        hide: true,
      },
      {
        field: "area_id",
        headerName: "area_id",
        hide: true,
      },
      {
        field: "work_location_id",
        headerName: "work_location_id",
        hide: true,
      },
      {
        field: "m18_product_code",
        headerName: "M18 code",
        flex: 1,
      },
      {
        field: groupName,
        headerName: "Product / Brand Name",
        flex: 1,
      },
      {
        field: "amount",
        headerName: "Amount",
        type: "number",
        align: "right",
        headerAlign: "right",
        flex: 1,
        valueFormatter: (value) =>
          `${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "HKD",
          }).format(value)}`,
      },
      {
        field: "total_amount",
        headerName: "Total Amount",
        type: "number",
        align: "right",
        headerAlign: "right",
        flex: 1,
        valueFormatter: (value) =>
          `${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "HKD",
          }).format(value)}`,
      },
      {
        field: "proportion_amount",
        headerName: "Proportion of Amount",
        align: "right",
        headerAlign: "right",
        flex: 1,
        valueGetter: (value, row) => {
          return (parseFloat(row.amount) / parseFloat(row.total_amount)) * 100;
        },
        valueFormatter: (value) =>
          `${new Intl.NumberFormat("en", { minimumFractionDigits: 1 }).format(
            _.round(value, 1)
          )}%`,
      },
      {
        field: "qty",
        headerName: "Quantity",
        align: "right",
        headerAlign: "right",
        flex: 1,
        valueFormatter: (value) => `${new Intl.NumberFormat().format(value)}`,
      },

      {
        field: "total_qty",
        headerName: "Total Quantity",
        align: "right",
        headerAlign: "right",
        flex: 1,
        valueFormatter: (value) => `${new Intl.NumberFormat().format(value)}`,
      },
      {
        field: "proportion_qty",
        headerName: "Proportion of Quantity",
        align: "right",
        headerAlign: "right",
        flex: 1,
        valueGetter: (value, row) => {
          return (parseFloat(row.qty) / parseFloat(row.total_qty)) * 100;
        },
        valueFormatter: (value) =>
          `${new Intl.NumberFormat("en", { minimumFractionDigits: 1 }).format(
            _.round(value, 1)
          )}%`,
      },
      {
        field: "actions",
        headerName: "",
        renderCell: (params) => {
          return (
            <React.Fragment>
              <IconButton
                size="small"
                color={"primary"}
                onClick={async () => {
                  const selectedFilter = {
                    ...filter,
                    product: [params.row.product_master_id],
                    dateGrouping: "day",
                    district:
                      filter.groupBy === "work_district"
                        ? [params.row.district_id]
                        : [],
                    area:
                      filter.groupBy === "work_area"
                        ? [params.row.area_id]
                        : [],
                    shop:
                      filter.groupBy === "work_shortcode,work_name"
                        ? [params.row.work_location_id]
                        : [],
                  };

                  await fetchProductTrend(selectedFilter);
                }}
              >
                <TrendingUpIcon fontSize="inherit" />
              </IconButton>
            </React.Fragment>
          );
        },
      },
    ];
    const CompareLineChart = (props) => {
      const lineChartData = useSelector(
        (state) => state.productSales.lineChartData
      );
      const lineChartOpen = useSelector(
        (state) => state.productSales.lineChartOpen
      );

      const [groupByData, setGroupByData] = React.useState("day");
      return (
        <Dialog
          maxWidth={"lg"}
          fullWidth
          open={lineChartOpen}
          onClose={() => dispatch(setLineChartOpen({ value: false }))}
        >
          <DialogContent>
            <Select
              labelId="groupByData-select-label"
              id="groupByData-select"
              label="date"
              value={groupByData}
              onChange={(e) => {
                setGroupByData(e.target.value);
                handleTrendData(rawSalesData, e.target.value);
              }}
            >
              <MenuItem value={"day"}>天</MenuItem>
              <MenuItem value={"week"}>週</MenuItem>
              <MenuItem value={"month"}>月</MenuItem>
              <MenuItem value={"quarter"}>季度</MenuItem>
            </Select>
            <LineChart
              margin={{ right: 5, left: 80 }}
              grid={{ vertical: true, horizontal: true }}
              {...lineChartData}
              sx={{
                [`.${lineElementClasses.root}, .${markElementClasses.root}`]: {
                  strokeWidth: 1,
                },
                ".MuiLineElement-series-total": {
                  strokeDasharray: "3 4 5 2",
                },
                [`.${markElementClasses.root}:not(.${markElementClasses.highlighted})`]:
                  {
                    fill: "#fff",
                  },
                [`& .${markElementClasses.highlighted}`]: {
                  stroke: "none",
                },
              }}
            ></LineChart>
          </DialogContent>
        </Dialog>
      );
    };
    return (
      <React.Fragment>
        <Dialog
          fullWidth
          maxWidth={"lg"}
          open={distributionDataOpen}
          onClose={handleOnClose}
        >
          <DialogTitle>{distributionDataTableTitle}</DialogTitle>
          <DialogContent style={{ paddingTop: 10 }}>
            <DataTable
              loading={distributionDataLoading}
              checkboxSelection={true}
              disableRowSelectionOnClick={true}
              getRowClassName={({ row }) => {
                if (filter.product.includes(row.product_master_id))
                  return `distributionTable--Selected`;
              }}
              ref={distributionTableRef}
              ariaLabel={"distributionTable"}
              ariaLabelledby={"distributionTable"}
              Toolbar={CompareButton}
              disableColumnSelector={true}
              getRowId={(row) => row[groupId]}
              rows={distributionData}
              columns={distributionColumns}
              pageSize={25}
              pinFirstColumn={false}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    m18_product_code: groupId === "product_master_id",
                    region_id: false,
                    area_id: false,
                    district_id: false,
                    work_location_id: false,
                    product_master_id: false,
                    brand_id: false,
                  },
                },
                sorting: {
                  sortModel: [{ field: "proportion_amount", sort: "desc" }],
                },
              }}
            />
          </DialogContent>
        </Dialog>
        <CompareLineChart></CompareLineChart>
      </React.Fragment>
    );
  });
  const handleDistricthange = (e) => {
    if (e.target.value.includes("ALL")) {
      const allDistrictOptions = _.map(districtOptions, "district_id");
      setFilter((prev) => {
        return {
          ...prev,
          district:
            e.target.value.length - 1 === allDistrictOptions.length
              ? []
              : allDistrictOptions,
          area: _.map(areaOptions, "area_id"),
          shop: [],
        };
      });
      setFilterAreaOptions(areaOptions);
      setFilterShopOptions(shopOptions);
    } else {
      const filterArea = areaOptions.filter((areaOption) =>
        e.target.value.includes(areaOption.district_id)
      );
      const filterShop = shopOptions.filter((shopOption) =>
        e.target.value.includes(shopOption.work_district_id)
      );
      setFilter((prev) => {
        return {
          ...prev,
          district: e.target.value,
          area: _.map(filterArea, (a) => a.area_id),
        };
      });
      setFilterAreaOptions(filterArea);
      setFilterShopOptions(filterShop);
    }
  };
  const handleAreaChange = (e) => {
    if (e.target.value.includes("ALL")) {
      const filterShop = shopOptions.filter((shopOption) =>
        filter.area.includes(shopOption.work_area_id)
      );
      setFilter((prev) => {
        return {
          ...prev,
          area:
            e.target.value.length - 1 === filterAreaOptions.length
              ? []
              : _.map(filterAreaOptions, (a) => a.area_id),
          shop: [],
        };
      });
      setFilterShopOptions(filterShop);
    } else {
      const filterShop = shopOptions.filter((shopOption) =>
        e.target.value.includes(shopOption.work_area_id)
      );
      setFilter((prev) => {
        return {
          ...prev,
          area: e.target.value,
          shop: _.map(filterShop, (a) => a.id),
        };
      });
      setFilterShopOptions(filterShop);
    }
  };

  //Fetch all
  const fetchAll = async () => {
    const { data: locations } = await getAreaAndDistrict();
    const { data: products } = await getProducts();
    const { data: workOptions } = await getRegionOptions();
    setAllOptions(workOptions);

    const district = _.map(_.uniqBy(workOptions, "district_id"), (option) => ({
      district_id: option.district_id,
      district_name: option.district_name,
    }));
    const areaManOptions = _.map(
      _.uniqBy(workOptions, "user_id"),
      (option) => ({
        am_id: option.user_id,
        am_name: option.full_name,
      })
    );
    setAmOptions(areaManOptions);

    setDistrictOptions(district);
    setProductOptions(products);
    const unArea = _.uniqBy(workOptions, "area_id");
    setFilter((prev) => {
      return {
        ...prev,
        district: _.map(district, "district_id"),
        area: _.map(unArea, "area_id"),
        am: _.map(areaManOptions, "am_id"),
      };
    });
    setShopOptions(locations);
    setFilterShopOptions(locations);

    setAreaOptions(unArea);
    setFilterAreaOptions(unArea);
  };
  const columns = [
    {
      field: "region_id",
      headerName: "region_id",
      hide: true,
    },
    {
      field: "district_id",
      headerName: "district_id",
      hide: true,
    },
    {
      field: "area_id",
      headerName: "area_id",
      hide: true,
    },
    {
      field: "work_location_id",
      headerName: "work_location_id",
      hide: true,
    },
    {
      field: "label",
      headerName: "Location",
      flex: 1,
    },
    {
      field: "amount",
      headerName: "Amount",
      type: "number",
      align: "right",
      headerAlign: "right",
      flex: 1,
      valueFormatter: (value) =>
        `${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "HKD",
        }).format(value)}`,
    },
    {
      field: "total_amount",
      headerName: "Total Amount",
      type: "number",
      align: "right",
      headerAlign: "right",
      flex: 1,
      valueFormatter: (value) =>
        `${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "HKD",
        }).format(value)}`,
    },
    {
      field: "proportion_amount",
      headerName: "Proportion of Amount",
      align: "right",
      headerAlign: "right",
      flex: 1,
      valueGetter: (value, row) => {
        return (parseFloat(row.amount) / parseFloat(row.total_amount)) * 100;
      },
      valueFormatter: (value) =>
        `${new Intl.NumberFormat("en", { minimumFractionDigits: 1 }).format(
          _.round(value, 1)
        )}%`,
    },
    {
      field: "qty",
      headerName: "Quantity",
      align: "right",
      headerAlign: "right",
      flex: 1,
      valueFormatter: (value) => `${new Intl.NumberFormat().format(value)}`,
    },

    {
      field: "total_qty",
      headerName: "Total Quantity",
      align: "right",
      headerAlign: "right",
      flex: 1,
      valueFormatter: (value) => `${new Intl.NumberFormat().format(value)}`,
    },
    {
      field: "proportion_qty",
      headerName: "Proportion of Quantity",
      align: "right",
      headerAlign: "right",
      flex: 1,
      valueGetter: (value, row) => {
        return (parseFloat(row.qty) / parseFloat(row.total_qty)) * 100;
      },
      valueFormatter: (value) =>
        `${new Intl.NumberFormat("en", { minimumFractionDigits: 1 }).format(
          _.round(value, 1)
        )}%`,
    },
  ];

  React.useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    if (filter.product.length > 0) {
      handleData(rowData, filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, ordering, limit]);
  return (
    <Layout header="DailyPage" selectedIndex={32} open={open} setOpen={setOpen}>
      <Title title="產品銷售統計" />
      <ThemeProvider theme={theme}>
        <Accordion
          expanded={expanded}
          style={{ flexGrow: 1, display: "block" }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                onClick={() => setExpanded(!expanded)}
              ></ExpandMoreIcon>
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <SearchIcon></SearchIcon>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                  lg: 3
                }}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">時間</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={dateRange}
                    label="date"
                    onChange={handDateRangeChange}
                  >
                    <MenuItem value={"7 days"}>最近7天</MenuItem>
                    <MenuItem value={"30 days"}>最近30天</MenuItem>
                    <MenuItem value={"6 months"}>最近半年</MenuItem>
                    <MenuItem value={"1 years"}>最近1年</MenuItem>
                    <MenuItem value={"custom"}>自訂</MenuItem>
                  </Select>
                  {showRange && (
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      <Grid
                        size={{
                          xs: 12,
                          md: 12,
                          lg: 12
                        }}>
                        <DatePicker
                          disableFuture
                          maxDate={filter.endDate}
                          label="開始"
                          value={filter.startDate}
                          components={{
                            OpenPickerIcon: () => (
                              <CalendarMonthIcon ref={dataPickerStartRef} />
                            ),
                          }}
                          onChange={(newValue) => {
                            setFilter((prev) => {
                              return {
                                ...prev,
                                startDate:
                                  moment(newValue).format("YYYY-MM-DD"),
                                endDate: moment(newValue)
                                  .endOf("months")
                                  .format("YYYY-MM-DD"),
                              };
                            });
                            dataPickerEndRef.current.parentElement.click();
                          }}
                          renderInput={(params) => (
                            <TextField {...params} sx={{ width: "100%" }} />
                          )}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          md: 12,
                          lg: 12
                        }}>
                        <DatePicker
                          disableFuture
                          minDate={filter.startDate}
                          label="結束"
                          value={filter.endDate}
                          components={{
                            OpenPickerIcon: () => (
                              <CalendarMonthIcon ref={dataPickerEndRef} />
                            ),
                          }}
                          onChange={(newValue) => {
                            setFilter((prev) => {
                              return {
                                ...prev,
                                endDate: moment(newValue).format("YYYY-MM-DD"),
                              };
                            });
                          }}
                          renderInput={(params) => (
                            <TextField {...params} sx={{ width: "100%" }} />
                          )}
                        />
                      </Grid>
                    </Grid>
                  )}
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2,
                  lg: 2
                }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12
                  }}>
                  <FormControl fullWidth>
                    <InputLabel id="am-select-label">AM</InputLabel>
                    <Select
                      labelId="am-select-label"
                      id="am-select"
                      value={filter.am}
                      label="am"
                      onChange={handleAMChange}
                      multiple
                      renderValue={(selected) =>
                        selected.length === amOptions.length
                          ? "所有AM"
                          : `已選擇${selected.length}個`
                      }
                      input={<OutlinedInput label="AM" />}
                    >
                      {_.map(amOptions, (item, index) => (
                        <MenuItem key={index} value={item.am_id}>
                          <Checkbox
                            checked={
                              filter.am.find((d) => d === item.am_id) !==
                              undefined
                            }
                          />
                          <ListItemText primary={item.am_name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  sx={{ mt: 2 }}
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12
                  }}>
                  <FormControl fullWidth>
                    <InputLabel id="district-select-label">地區</InputLabel>
                    <Select
                      labelId="district-select-label"
                      id="district-select"
                      value={filter.district}
                      label="district"
                      multiple
                      onChange={handleDistricthange}
                      renderValue={(selected) =>
                        selected.length === districtOptions.length
                          ? "所有地區"
                          : `已選擇${selected.length}個`
                      }
                      input={<OutlinedInput label="地區" />}
                    >
                      <MenuItem key={"ALL"} value={"ALL"}>
                        <Checkbox
                          checked={
                            filter.district.length === districtOptions.length
                          }
                        />
                        <ListItemText primary={"所有地區"} />
                      </MenuItem>
                      {_.map(districtOptions, (item, index) => (
                        <MenuItem key={index} value={item.district_id}>
                          <Checkbox
                            checked={
                              filter.district.find(
                                (d) => d === item.district_id
                              ) !== undefined
                            }
                          />
                          <ListItemText primary={item.district_name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  sx={{ mt: 2 }}
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12
                  }}>
                  <FormControl fullWidth>
                    <InputLabel id="area-select-label">區域</InputLabel>
                    <Select
                      labelId="area-select-label"
                      id="area-select"
                      value={filter.area}
                      multiple
                      label="area"
                      onChange={handleAreaChange}
                      renderValue={(selected) =>
                        selected.length === filterAreaOptions.length
                          ? "所有區域"
                          : `已選擇${selected.length}個`
                      }
                      input={<OutlinedInput label="區域" />}
                    >
                      <MenuItem key={"ALL"} value={"ALL"}>
                        <Checkbox
                          checked={
                            filter.area.length === filterAreaOptions.length
                          }
                        />
                        <ListItemText primary={"所有區域"} />
                      </MenuItem>
                      {_.map(filterAreaOptions, (item, index) => (
                        <MenuItem key={index} value={item.area_id}>
                          <Checkbox
                            checked={
                              filter.area.find((d) => d === item.area_id) !==
                              undefined
                            }
                          />
                          <ListItemText primary={item.area_name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 7,
                  lg: 7
                }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12
                  }}>
                  <FormControl fullWidth>
                    <Autocomplete
                      disableCloseOnSelect
                      limitTags={4}
                      groupBy={(option) =>
                        `${option.district_name}-${option.area_name}`
                      }
                      multiple
                      getOptionKey={(option) => option.id}
                      getOptionLabel={(option) =>
                        `${option.work_name}(${option.work_shortcode})`
                      }
                      onChange={(event, newValues) => {
                        const ids = _.map(newValues, "id");
                        setFilter((prev) => {
                          return {
                            ...prev,
                            shop: ids,
                          };
                        });
                      }}
                      id="shop-combo-box"
                      options={_.filter(
                        filterShopOptions,
                        (filterShopOption) => {
                          return _.includes(
                            filter.area,
                            filterShopOption.work_area_id
                          );
                        }
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="店鋪" />
                      )}
                      renderOption={(props, option, { selected }) => {
                        return (
                          <li {...props} key={`shop-${option.work_shortcode}`}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.work_name}({option.work_shortcode})
                          </li>
                        );
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  sx={{ mt: 2 }}
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12
                  }}>
                  <FormControl fullWidth>
                    <ProductAutoComplete
                      multiple={true}
                      selectByCategory
                      limitTags={2}
                      selectedValue={filter.product}
                      productOptions={productOptions}
                      handleOnChange={(value) => {
                        if (_.includes(value, "ALL")) {
                          if (value.length === productOptions.length + 1) {
                            setFilter((prev) => {
                              return {
                                ...prev,
                                product: [],
                              };
                            });
                          } else {
                            setFilter((prev) => {
                              return {
                                ...prev,
                                product: _.map(productOptions, "id"),
                              };
                            });
                          }
                        } else {
                          setFilter((prev) => {
                            return {
                              ...prev,
                              product: value,
                            };
                          });
                        }
                        // if (newValue.length >= 10) {
                        //   setProductAutoCompleteOpen(false);
                        // } else {

                        // if (newValue.length === 10) {
                        //   setProductAutoCompleteOpen(false);
                        // }
                        //}
                      }}
                      error={filter.product.length <= 0}
                    ></ProductAutoComplete>
                  </FormControl>
                </Grid>
                <Grid
                  container
                  sx={{ mt: 2 }}
                  size={{
                    xs: 12,
                    md: 12,
                    lg: 12
                  }}>
                  <Grid
                    sx={{ mr: 2 }}
                    size={{
                      xs: 12,
                      md: 2,
                      lg: 2
                    }}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        分佈
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={filter.groupBy}
                        label="date"
                        onChange={handleGroupingChange}
                      >
                        <MenuItem value={"work_district"}>地區</MenuItem>
                        <MenuItem value={"work_area"}>區域</MenuItem>
                        <MenuItem value={"work_shortcode,work_name"}>
                          店鋪
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    sx={{ mr: 2 }}
                    size={{
                      xs: 12,
                      md: 2,
                      lg: 2
                    }}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        分類
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={filter.groupByType}
                        label="date"
                        onChange={handleGroupTypeChange}
                      >
                        <MenuItem value={"product"}>產品</MenuItem>
                        <MenuItem value={"brand"}>品牌</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    sx={{ mr: 2 }}
                    size={{
                      xs: 12,
                      md: 2,
                      lg: 2
                    }}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        數據來源
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="數據來源"
                        value={sources}
                        onChange={(e) => {
                          setSources(e.target.value);
                        }}
                      >
                        <MenuItem value={"promoter"}>即時數據</MenuItem>
                        <MenuItem value={"mannings"}>萬寧</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 2,
                      lg: 2
                    }}>
                    <ThemeProvider theme={ButtonSendTheme}>
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <LoadingButton
                          size="small"
                          onClick={() => {
                            setLoading(true);
                            fetchProductSales();
                            setSlideTranOn(true);
                          }}
                          endIcon={<SearchOutlinedIcon fontSize="small" />}
                          loading={isLoading}
                          disabled={filter.product.length <= 0}
                          loadingPosition="end"
                          variant="contained"
                        >
                          搜尋
                        </LoadingButton>
                      </div>
                    </ThemeProvider>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 1,
                  lg: 1
                }}></Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </ThemeProvider>
      <Fade in={slideTranOn} mountOnEnter unmountOnExit>
        <StyledPaper page={"yes"}>
          <ThemeProvider theme={TableListTheme}>
            <Box sx={{ width: "100%" }}>
              <Tabs
                value={displayMode}
                onChange={(event, newValue) => {
                  setDisplayMode(newValue);
                  setExpanded(false);
                }}
                centered
              >
                <Tab label="table" {...a11yProps(0)} />
                {/* <Tab label="chart" {...a11yProps(1)} /> */}
              </Tabs>
              <CustomTabPanel value={displayMode} index={0}>
                <DataTable
                  loading={dataTableLoading}
                  ariaLabel={"productSales"}
                  ariaLabelledby={"productSales"}
                  Toolbar={GridToolbar}
                  getRowId={(row) => row.label}
                  rows={rowData}
                  columns={columns}
                  pageSize={50}
                  pinFirstColumn={false}
                  initialState={{
                    columns: {
                      columnVisibilityModel: {
                        region_id: false,
                        area_id: false,
                        district_id: false,
                        work_location_id: false,
                      },
                    },
                    sorting: {
                      sortModel: [{ field: "proportion_amount", sort: "desc" }],
                    },
                  }}
                  onRowClick={async ({ row }) => {
                    const selectedFilter = {
                      ...filter,
                      product: [],
                      district:
                        filter.groupBy === "work_district"
                          ? [row.work_label_id]
                          : [],
                      area:
                        filter.groupBy === "work_area"
                          ? [row.work_label_id]
                          : [],
                      shop:
                        filter.groupBy === "work_shortcode,work_name"
                          ? [row.work_label_id]
                          : [],
                    };
                    onRowClick(selectedFilter, row.label);
                  }}
                />
              </CustomTabPanel>
            </Box>
            <DistributionTable></DistributionTable>
          </ThemeProvider>
        </StyledPaper>
      </Fade>
    </Layout>
  );
};

export default ProductSalesChartPage;
